import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { submitInquiry } from '@/lib/cms';

const cooperationTypes = [
  '量化策略合作',
  '基金管理业务',
  '家族办公室服务',
  '资本投资与并购',
  '金融科技合作',
  '媒体与公共关系',
  '人才招聘',
  '其他咨询',
];

function LiveResponseIndicator() {
  const [hkTime, setHkTime] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [pulseOn, setPulseOn] = useState(true);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hk = now.toLocaleString('zh-HK', {
        timeZone: 'Asia/Hong_Kong',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      const day = now.toLocaleString('zh-HK', {
        timeZone: 'Asia/Hong_Kong',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      setHkTime(`${day} ${hk} HKT`);
    };
    updateTime();
    const t = setInterval(updateTime, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setElapsed((e) => e + 1);
      setPulseOn((p) => !p);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 px-4 py-3 rounded-xl bg-background-100 border border-background-300/30 scan-line">
      {/* Response Time */}
      <div className="flex items-center gap-2.5">
        <div className="relative w-8 h-8 flex items-center justify-center">
          <span className={`absolute inset-0 rounded-full ${pulseOn ? 'bg-emerald-400/20' : 'bg-emerald-400/5'} transition-colors duration-700`}></span>
          <i className="ri-flashlight-line text-emerald-400 text-base relative z-10"></i>
        </div>
        <div>
          <p className="text-[10px] mono-data text-foreground-600 uppercase tracking-wider">Avg Response</p>
          <p className="text-sm mono-data font-bold text-emerald-400 tabular-nums">
            {'<'} 2h
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ml-1.5 animate-pulse-glow align-middle"></span>
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden sm:block w-px h-8 bg-background-300/40"></div>

      {/* HK Time */}
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200/50 border border-background-300/30">
          <i className="ri-time-line text-accent-400 text-sm"></i>
        </div>
        <div>
          <p className="text-[10px] mono-data text-foreground-600 uppercase tracking-wider">Hong Kong Office</p>
          <p className="text-sm mono-data font-bold text-foreground-200 tabular-nums">{hkTime}</p>
        </div>
      </div>

      {/* Subtle: online agents count */}
      <div className="hidden lg:flex items-center gap-1.5 ml-auto">
        <div className="flex -space-x-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-5 h-5 rounded-full border border-background-100 flex items-center justify-center"
              style={{ backgroundColor: ['#48D1B0', '#60A8E0', '#C8A840'][i], opacity: 0.25 + i * 0.05 }}
            >
              <span className="text-[6px] text-background-950 font-bold">{['A', 'K', 'S'][i]}</span>
            </div>
          ))}
        </div>
        <span className="text-[9px] mono-data text-foreground-600">3 agents online</span>
      </div>
    </div>
  );
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    setSubmitting(true);

    try {
      const form = e.currentTarget;
      const formData: Record<string, string> = {};
      const fields = ['name', 'company', 'email', 'phone', 'cooperation_type', 'message'];
      fields.forEach((field) => {
        const el = form.elements.namedItem(field) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        if (el && el.value) {
          formData[field] = el.value;
        }
      });

      await submitInquiry(formData);

      setSubmitted(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败，请稍后再试或直接发送邮件至 info@antchain-holdings.hk');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-25"></div>
        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">Contact Us</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">联系我们</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              欢迎业务合作、客户咨询、媒体联络与人才加盟。我们将尽快与您取得联系。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Contact Info */}
              <div className="lg:col-span-2">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-8">联系方式</h2>
                <div className="space-y-8">
                  {[
                    { icon: 'ri-map-pin-line', title: '集团地址', content: '香港中环金融街8号\n国际金融中心二期 38楼' },
                    { icon: 'ri-phone-line', title: '联系电话', content: '+852 2801 6800' },
                    { icon: 'ri-mail-line', title: '电子邮箱', content: 'info@antchain-holdings.hk' },
                    { icon: 'ri-time-line', title: '办公时间', content: '周一至周五 09:00 - 18:00（香港时间）' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/15 flex items-center justify-center flex-shrink-0 group-hover:border-accent-500/30 transition-colors duration-300">
                        <i className={`${item.icon} text-lg text-primary-400 group-hover:text-accent-400 transition-colors duration-300`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-200 mb-1">{item.title}</h4>
                        <p className="text-sm text-foreground-500 leading-relaxed font-light whitespace-pre-line">{item.content}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Compliance notice */}
                <div className="mt-10 p-6 rounded-xl bg-background-50 border border-background-300/30">
                  <div className="flex items-start gap-2.5">
                    <i className="ri-information-line text-accent-500 mt-0.5"></i>
                    <p className="text-xs text-foreground-600 leading-relaxed font-light">
                      <strong className="text-foreground-400">合规提示：</strong>投资有风险，过往业绩不预示未来表现。本网站所载信息仅供一般参考，不构成任何投资建议、要约或承诺。在作出任何投资决定前，请咨询专业顾问并仔细阅读相关法律文件。
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                {/* —— Live Response Indicator —— */}
                <div className="mb-3">
                  <LiveResponseIndicator />
                </div>

                {/* —— Today Inquiry Stats —— */}
                <div className="mb-5">
                  <TodayInquiryStats />
                </div>

                <div className="bg-background-50 rounded-xl border border-background-300/30 p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-2">业务合作与客户咨询</h2>
                  <p className="text-sm text-foreground-500 mb-8 font-light">请填写以下信息，我们将尽快与您取得联系。</p>

                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center">
                        <i className="ri-check-line text-2xl text-accent-400"></i>
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground-100 mb-2">感谢您的咨询</h3>
                      <p className="text-sm text-foreground-500 font-light">我们已收到您的信息，将在1-2个工作日内与您联系。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            姓名 <span className="text-accent-400">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            公司 / 机构
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200"
                            placeholder="请输入公司或机构名称"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            电子邮箱 <span className="text-accent-400">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200"
                            placeholder="请输入电子邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-foreground-300 mb-1.5">
                            联系电话
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200"
                            placeholder="请输入联系电话"
                          />
                        </div>
                      </div>

                      <div className="mt-5">
                        <label htmlFor="cooperation_type" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          合作类型 <span className="text-accent-400">*</span>
                        </label>
                        <select
                          id="cooperation_type"
                          name="cooperation_type"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200 appearance-none cursor-pointer"
                          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '36px' }}
                        >
                          <option value="">请选择合作类型</option>
                          {cooperationTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mt-5">
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          留言内容 <span className="text-accent-400">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300/50 bg-background-100 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all duration-200 resize-none"
                          placeholder="请简要描述您的合作需求或咨询内容（限500字以内）"
                        ></textarea>
                        <p className="mt-1 text-xs text-foreground-600 font-light">限500字以内</p>
                      </div>

                      {error && (
                        <div className="mt-4 p-3 rounded-lg bg-background-200 border border-red-500/20 text-sm text-red-400">
                          {error}
                        </div>
                      )}

                      <div className="mt-6">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="flex items-center gap-2 px-8 py-3 bg-accent-500 text-background-950 font-bold text-sm rounded-full hover:bg-accent-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap shadow-[0_0_20px_rgba(200,160,80,0.12)] hover:shadow-[0_0_30px_rgba(200,160,80,0.2)] cursor-pointer"
                        >
                          {submitting ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              提交中...
                            </>
                          ) : (
                            <>
                              提交咨询
                              <i className="ri-send-plane-line"></i>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Today Inquiry Stats ── */
function TodayInquiryStats() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { formatted } = useCountUp({ target: 47, duration: 2000, enabled: visible });

  return (
    <div ref={ref} className="flex items-center gap-4 px-4 py-2.5 rounded-xl bg-background-100 border border-background-300/30">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-glow"></span>
        <span className="text-[10px] mono-data text-foreground-600 tracking-wider">TODAY INQUIRIES</span>
      </div>
      <span className={`text-sm mono-data font-bold text-foreground-100 tabular-nums transition-colors duration-300 ${visible ? 'text-foreground-100' : 'text-foreground-600'}`}>
        {visible ? formatted : '--'}
      </span>
      <div className="hidden sm:flex items-center gap-3 ml-auto">
        <div className="flex items-center gap-1">
          <span className="text-[9px] mono-data text-foreground-600">RESOLVED</span>
          <span className="text-[10px] mono-data font-bold text-emerald-400">42</span>
        </div>
        <div className="w-px h-3 bg-background-300/40"></div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] mono-data text-foreground-600">PENDING</span>
          <span className="text-[10px] mono-data font-bold text-accent-400">5</span>
        </div>
        <div className="w-px h-3 bg-background-300/40"></div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] mono-data text-foreground-600">SLA</span>
          <span className="text-[10px] mono-data font-bold text-primary-400">100%</span>
        </div>
      </div>
    </div>
  );
}
